import React from "react";
import {
  Box,
  Typography,
  TextField,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormHelperText,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  getValueByPath,
  extractPlaceholders,
  containsHtml,
} from "./translationsHelpers";

const TranslationsEditorKeyDetail = ({
  keyPath,
  translationsJson,
  languages,
  onChange,
  getMissingCount,
}) => {
  const { t } = useTranslation();
  const refLang = languages.includes("en") ? "en" : languages[0];
  const refPlaceholders =
    refLang && languages.length
      ? extractPlaceholders(
          getValueByPath(translationsJson, refLang, keyPath) || ""
        )
      : [];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="subtitle2" color="text.secondary">
        {keyPath}
      </Typography>
      {languages.map((lang) => {
        const value = getValueByPath(translationsJson, lang, keyPath);
        const str = value != null ? String(value) : "";
        const missing = str.trim() === "";
        const placeholders = extractPlaceholders(str);
        const placeholderMismatch =
          refPlaceholders.length > 0 &&
          (placeholders.length !== refPlaceholders.length ||
            refPlaceholders.some((p) => !placeholders.includes(p)));
        const hasHtml = containsHtml(str);

        return (
          <Box key={lang}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Typography variant="caption" fontWeight={600}>
                {lang.toUpperCase()}
              </Typography>
              {missing && (
                <Chip
                  size="small"
                  label={t("dsokCustomerDetail.translationsUI.missingTranslation")}
                  color="warning"
                />
              )}
              {hasHtml && (
                <Chip size="small" label="HTML" color="info" />
              )}
            </Box>
            <TextField
              fullWidth
              multiline
              minRows={2}
              value={str}
              onChange={(e) => onChange(lang, keyPath, e.target.value)}
              error={placeholderMismatch}
              size="small"
            />
            {placeholderMismatch && (
              <FormHelperText error>
                {t("dsokCustomerDetail.translationsUI.placeholderMismatch")}
              </FormHelperText>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

const TranslationsEditorGroupGrid = ({
  groupKeyPath,
  leafKeyPaths,
  translationsJson,
  languages,
  onChange,
  getMissingCount,
}) => {
  const { t } = useTranslation();

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{
        maxHeight: "calc(100vh - 280px)",
        minHeight: 320,
        overflow: "auto",
      }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                fontWeight: 600,
                backgroundColor: "background.paper",
                position: "sticky",
                left: 0,
                zIndex: 2,
                minWidth: 140,
                boxShadow: "2px 0 4px -2px rgba(0,0,0,0.1)",
              }}
            >
              {t("dsokCustomerDetail.translationsUI.key")}
            </TableCell>
            {languages.map((lang) => (
              <TableCell
                key={lang}
                sx={{
                  fontWeight: 600,
                  backgroundColor: "background.paper",
                  whiteSpace: "nowrap",
                }}
              >
                {lang.toUpperCase()}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {leafKeyPaths.map((keyPath) => {
            const shortKey = groupKeyPath ? keyPath.replace(`${groupKeyPath}.`, "") : keyPath;
            return (
              <TableRow key={keyPath}>
                <TableCell
                  sx={{
                    fontFamily: "monospace",
                    fontSize: "0.8rem",
                    backgroundColor: "background.paper",
                    position: "sticky",
                    left: 0,
                    zIndex: 1,
                    minWidth: 140,
                    boxShadow: "2px 0 4px -2px rgba(0,0,0,0.08)",
                  }}
                >
                  {shortKey}
                </TableCell>
                {languages.map((lang) => {
                  const value = getValueByPath(translationsJson, lang, keyPath);
                  const str = value != null ? String(value) : "";
                  const missing = str.trim() === "";
                  return (
                    <TableCell key={lang}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <TextField
                          fullWidth
                          size="small"
                          value={str}
                          onChange={(e) => onChange(lang, keyPath, e.target.value)}
                          error={missing}
                          sx={{ "& .MuiInputBase-input": { fontSize: "0.8rem" } }}
                        />
                        {missing && (
                          <Chip
                            size="small"
                            label={t("dsokCustomerDetail.translationsUI.missing")}
                            color="warning"
                            sx={{ flexShrink: 0 }}
                          />
                        )}
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const TranslationsEditor = ({
  selectedKeyPath,
  selectedGroupKeyPath,
  leafKeyPathsInGroup,
  translationsJson,
  languages,
  onChange,
  getMissingCount,
}) => {
  const { t } = useTranslation();

  if (selectedKeyPath) {
    return (
      <Box sx={{ p: 2 }}>
        <TranslationsEditorKeyDetail
          keyPath={selectedKeyPath}
          translationsJson={translationsJson}
          languages={languages}
          onChange={onChange}
          getMissingCount={getMissingCount}
        />
      </Box>
    );
  }

  if (selectedGroupKeyPath != null && leafKeyPathsInGroup?.length > 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          {selectedGroupKeyPath}
        </Typography>
        <TranslationsEditorGroupGrid
          groupKeyPath={selectedGroupKeyPath}
          leafKeyPaths={leafKeyPathsInGroup}
          translationsJson={translationsJson}
          languages={languages}
          onChange={onChange}
          getMissingCount={getMissingCount}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, color: "text.secondary" }}>
      <Typography variant="body2">
        {t("dsokCustomerDetail.translationsUI.selectKeyOrGroup")}
      </Typography>
    </Box>
  );
};

export default TranslationsEditor;
